import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, CopyCheck, Loader2, RefreshCw, Search, X } from "lucide-react";
import { L } from "@/i18n";
import { errorMessage } from "@/lib/api";
import { useDuplicates } from "@/hooks/useDuplicates";
import { DuplicateSummary } from "@/components/duplicates/DuplicateSummary";
import { DuplicateGroup } from "@/components/duplicates/DuplicateGroup";
import { Pager } from "@/components/users/Pager";
import { EditQuestionDialog } from "@/components/questions/EditQuestionDialog";

const PAGE_SIZE = 10;

export default function Duplicates() {
  const [module, setModule] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null);
  const { data, isLoading, isFetching, error, refetch } = useDuplicates();

  const groups = useMemo(() => {
    const q = search.trim().toLowerCase();
    const all = (data?.groups ?? []).filter((g) => module === "all" || g.parents?.includes(module));
    return q ? all.filter((g) => g.items?.some((i) => i.question_text?.toLowerCase().includes(q))) : all;
  }, [data, search, module]);

  useEffect(() => setPage(1), [module, search]);

  const pages = Math.max(1, Math.ceil(groups.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const slice = groups.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
  const goTo = (p) => { setPage(p); window.scrollTo({ top: 0 }); };

  return (
    <div data-testid="duplicates-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-amber-500">{L("Catalog quality", "Qualidade do catálogo")}</p>
          <h1 className="mt-2 font-heading text-3xl sm:text-4xl font-bold text-slate-50 tracking-tight">{L("Duplicates", "Duplicados")}</h1>
          <p className="mt-2 text-sm text-slate-400">{L("Questions with the same text inside the same submodule. Select the unneeded copies and delete them.", "Perguntas com o mesmo texto dentro do mesmo submódulo. Selecione as cópias desnecessárias e elimine-as.")}</p>
        </div>
        <button onClick={() => refetch()} disabled={isFetching} className="self-start sm:self-auto flex items-center gap-2 rounded-md border border-slate-700 px-3 py-2 text-xs text-slate-300 hover:border-amber-500/50 hover:text-amber-400 transition-colors disabled:opacity-60" data-testid="duplicates-refresh-button">
          <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} /> {L("Refresh", "Atualizar")}
        </button>
      </div>

      {isLoading && <div className="py-24 grid place-items-center" data-testid="duplicates-loading"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>}
      {error && !isLoading && (
        <div className="rounded-lg border border-rose-500/30 bg-rose-500/5 p-8 text-center text-sm text-rose-300" data-testid="duplicates-error">
          <AlertTriangle className="mx-auto mb-2 h-5 w-5" /> {errorMessage(error)}
        </div>
      )}
      {data && (
        <>
          <DuplicateSummary data={data} module={module} onModule={setModule} />
          <div className="relative mb-5">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={L("Search duplicate question text…", "Pesquisar no texto das perguntas duplicadas…")}
              className="w-full h-11 rounded-md bg-[#0f172a] border border-slate-700 pl-10 pr-9 text-sm text-slate-100 placeholder:text-slate-600 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 transition-colors"
              data-testid="duplicates-search-input"
            />
            {search && (
              <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-200" data-testid="duplicates-search-clear" aria-label={L("Clear", "Limpar")}>
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          {slice.length === 0 ? (
            <div className="rounded-lg border border-dashed border-slate-700 p-12 text-center" data-testid="duplicates-empty">
              <CopyCheck className="mx-auto h-7 w-7 text-emerald-500" />
              <p className="mt-3 text-sm text-slate-300">{L("No duplicate groups found.", "Nenhum grupo duplicado encontrado.")}</p>
            </div>
          ) : (
            <div className="space-y-5" data-testid="duplicates-list">
              {slice.map((g) => <DuplicateGroup key={g.hash} group={g} onEdit={setEditing} />)}
            </div>
          )}
          {groups.length > 0 && (
            <div className="mt-5 rounded-lg bg-[#131f37] border border-[#1e293b]">
              <Pager prefix="duplicates" page={current} pages={pages} total={groups.length} from={(current - 1) * PAGE_SIZE + 1} to={Math.min(current * PAGE_SIZE, groups.length)} onPage={goTo} />
            </div>
          )}
        </>
      )}
      <EditQuestionDialog question={editing} onClose={() => setEditing(null)} />
    </div>
  );
}
